import React from 'react'
import BannerImageEditor from './BannerImageEditor'
import CarouselEditor from './CarouselEditor'
import EditorialEditor from './EditorialEditor'
import FaqEditor from './FaqEditor'
import GoogleAdSlotEditor from './GoogleAdSlotEditor'
import HeroBannerEditor, { heroBannerSaveError } from './HeroBannerEditor'
import NotificationBannerEditor from './NotificationBannerEditor'
import PlanSelectionEditor from './PlanSelectionEditor'
import StandardLandscapeGridEditor from './StandardLandscapeGridEditor'
import TournamentCarouselEditor from './TournamentCarouselEditor'
import VideoEmbedEditor from './VideoEmbedEditor'

interface WidgetMetadataEditorProps {
  widgetType: string
  metadata: Record<string, any>
  onChange: (metadata: Record<string, any>) => void
  /** Widget id is available once the widget exists. Required for editors that upload assets. */
  widgetId?: string
}

/** Renders the appropriate metadata editor for a given widget type */
const WidgetMetadataEditor: React.FC<WidgetMetadataEditorProps> = ({ widgetType, metadata, onChange, widgetId }) => {
  switch (widgetType) {
    case 'video_embed':
      return <VideoEmbedEditor metadata={metadata} onChange={onChange} />
    case 'editorial':
      return <EditorialEditor metadata={metadata} onChange={onChange} />
    case 'banner_image':
      return <BannerImageEditor metadata={metadata} onChange={onChange} widgetId={widgetId} />
    case 'google_ad_slot':
      return <GoogleAdSlotEditor metadata={metadata} onChange={onChange} />
    case 'standard_landscape_carousel':
    case 'standard_portrait_carousel':
    case 'small_landscape_carousel':
    case 'large_landscape_carousel':
      return (
        <CarouselEditor
          metadata={metadata}
          onChange={onChange}
          widgetId={widgetId}
          widgetType={widgetType}
        />
      )
    case 'standard_landscape_grid':
      return (
        <StandardLandscapeGridEditor
          metadata={metadata}
          onChange={onChange}
          widgetId={widgetId}
          widgetType={widgetType}
        />
      )
    case 'tournament_carousel':
      return <TournamentCarouselEditor />
    case 'hero_banner':
      return <HeroBannerEditor metadata={metadata} onChange={onChange} widgetId={widgetId} />
    case 'notification_banner':
      return <NotificationBannerEditor metadata={metadata} onChange={onChange} />
    case 'faq':
      return <FaqEditor metadata={metadata} onChange={onChange} />
    case 'plan_selection':
      return <PlanSelectionEditor metadata={metadata} onChange={onChange} />
    default:
      return <p>No editor available for {widgetType}</p>
  }
}

/** Save gate for rules only the CMS enforces; null when the metadata may be saved. */
export const widgetMetadataSaveError = (widgetType: string, metadata: Record<string, unknown>): string | null => {
  switch (widgetType) {
    case 'hero_banner':
      return heroBannerSaveError(metadata)
    default:
      return null
  }
}

export default WidgetMetadataEditor
export { BannerImageEditor, CarouselEditor, EditorialEditor, FaqEditor, GoogleAdSlotEditor, HeroBannerEditor, NotificationBannerEditor, PlanSelectionEditor, StandardLandscapeGridEditor, TournamentCarouselEditor, VideoEmbedEditor }
